import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'
import type { AwaitingReplyFrom } from '@/features/conversations/types'

import type {
  AssistantTask,
  AssistantTaskOrigin,
  AssistantTaskStatus,
  TriageConversationItem,
  TriageOverview,
  TriagePriority,
  TriageSettings,
  TriageSettingsInput,
} from './types'

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

const PRIORITIES: readonly TriagePriority[] = ['P1', 'P2', 'P3']
const AWAITING: readonly AwaitingReplyFrom[] = ['none', 'owner', 'contact']
const TASK_STATUSES: readonly AssistantTaskStatus[] = ['open', 'done', 'dismissed']
const TASK_ORIGINS: readonly AssistantTaskOrigin[] = ['agent', 'owner']

function toPriority(value: string | null): TriagePriority | null {
  return value !== null && (PRIORITIES as readonly string[]).includes(value)
    ? (value as TriagePriority)
    : null
}

function toAwaiting(value: string): AwaitingReplyFrom {
  return (AWAITING as readonly string[]).includes(value) ? (value as AwaitingReplyFrom) : 'none'
}

function mapItem(
  dto: components['schemas']['TriageConversationItemResponse'],
): TriageConversationItem {
  return {
    conversationSeq: toNumber(dto.conversationSeq),
    contactName: dto.contactName,
    contactIsImportant: dto.contactIsImportant,
    awaitingReplyFrom: toAwaiting(dto.awaitingReplyFrom),
    priority: toPriority(dto.priority),
    priorityReason: dto.priorityReason,
    summary: dto.summary,
    need: dto.need,
    suggestedReply: dto.suggestedReply,
    unansweredSince: dto.unansweredSince,
    lastOutboundAt: dto.lastOutboundAt,
    lastMessagePreview: dto.lastMessagePreview,
    triageUpdatedAt: dto.triageUpdatedAt,
    triageExcluded: dto.triageExcluded,
  }
}

function mapTask(dto: components['schemas']['AssistantTaskResponse']): AssistantTask {
  return {
    id: dto.id,
    conversationSeq: dto.conversationSeq === null ? null : toNumber(dto.conversationSeq),
    contactName: dto.contactName,
    title: dto.title,
    details: dto.details,
    dueHint: dto.dueHint,
    status: (TASK_STATUSES as readonly string[]).includes(dto.status)
      ? (dto.status as AssistantTaskStatus)
      : 'open',
    origin: (TASK_ORIGINS as readonly string[]).includes(dto.origin)
      ? (dto.origin as AssistantTaskOrigin)
      : 'owner',
    sourceMessageSeqs: dto.sourceMessageSeqs.map(toNumber),
    createdAt: dto.createdAt,
    completedAt: dto.completedAt,
  }
}

function mapSettings(dto: components['schemas']['TriageSettingsResponse']): TriageSettings {
  return {
    enabled: dto.enabled,
    transcribeAudio: dto.transcribeAudio,
    quietWindowMinutes: toNumber(dto.quietWindowMinutes),
    historyWindowMessages: toNumber(dto.historyWindowMessages),
    historyWindowChars: toNumber(dto.historyWindowChars),
    dailyRunCap: toNumber(dto.dailyRunCap),
    digestEnabled: dto.digestEnabled,
    digestTime: dto.digestTime,
    timeZoneId: dto.timeZoneId,
    digestRecipientNumber: dto.digestRecipientNumber,
    lastDigestSentOnLocalDate: dto.lastDigestSentOnLocalDate,
    lastDigestErrorCode: dto.lastDigestErrorCode,
  }
}

function ensureOk(response: Response, error: unknown): void {
  if (!response.ok) {
    throw new ApiError(parseProblemDetails(error, response.status))
  }
}

/** Visão de Pendências: o que espera você, o que espera o contato e as tarefas abertas. */
export async function getTriageOverview(tenantId: string): Promise<TriageOverview> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/triage/overview', {
    params: { path: { tenantId } },
  })
  ensureOk(result.response, result.error)
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return {
    enabled: result.data.enabled,
    awaitingYou: result.data.awaitingYou.map(mapItem),
    awaitingThem: result.data.awaitingThem.map(mapItem),
    tasks: result.data.tasks.map(mapTask),
    counts: {
      awaitingYou: toNumber(result.data.counts.awaitingYou),
      awaitingThem: toNumber(result.data.counts.awaitingThem),
      openTasks: toNumber(result.data.counts.openTasks),
    },
  }
}

export async function dismissSuggestion(tenantId: string, conversationSeq: number): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/triage/conversations/{conversationSeq}/dismiss-suggestion',
    { params: { path: { tenantId, conversationSeq } } },
  )
  ensureOk(result.response, result.error)
}

export async function setTriageExclusion(
  tenantId: string,
  conversationSeq: number,
  excluded: boolean,
): Promise<void> {
  const result = await apiClient.PUT(
    '/api/v1/tenants/{tenantId}/triage/conversations/{conversationSeq}/exclusion',
    { params: { path: { tenantId, conversationSeq } }, body: { excluded } },
  )
  ensureOk(result.response, result.error)
}

export async function setContactImportant(
  tenantId: string,
  contactId: string,
  important: boolean,
): Promise<void> {
  const result = await apiClient.PUT(
    '/api/v1/tenants/{tenantId}/triage/contacts/{contactId}/important',
    { params: { path: { tenantId, contactId } }, body: { important } },
  )
  ensureOk(result.response, result.error)
}

export async function listTasks(
  tenantId: string,
  status: AssistantTaskStatus,
): Promise<readonly AssistantTask[]> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/triage/tasks', {
    params: { path: { tenantId }, query: { status } },
  })
  ensureOk(result.response, result.error)
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data.tasks.map(mapTask)
}

export async function createTask(
  tenantId: string,
  input: {
    readonly title: string
    readonly details?: string | null
    readonly conversationSeq?: number | null
  },
): Promise<AssistantTask> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/triage/tasks', {
    params: { path: { tenantId } },
    body: {
      title: input.title,
      details: input.details ?? null,
      conversationSeq: input.conversationSeq ?? null,
    },
  })
  ensureOk(result.response, result.error)
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapTask(result.data)
}

export async function completeTask(tenantId: string, taskId: string): Promise<void> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/triage/tasks/{taskId}/complete', {
    params: { path: { tenantId, taskId } },
  })
  ensureOk(result.response, result.error)
}

export async function dismissTask(tenantId: string, taskId: string): Promise<void> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/triage/tasks/{taskId}/dismiss', {
    params: { path: { tenantId, taskId } },
  })
  ensureOk(result.response, result.error)
}

export async function getTriageSettings(tenantId: string): Promise<TriageSettings> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/triage/settings', {
    params: { path: { tenantId } },
  })
  ensureOk(result.response, result.error)
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapSettings(result.data)
}

export async function updateTriageSettings(
  tenantId: string,
  input: TriageSettingsInput,
): Promise<TriageSettings> {
  const result = await apiClient.PUT('/api/v1/tenants/{tenantId}/triage/settings', {
    params: { path: { tenantId } },
    body: {
      enabled: input.enabled,
      transcribeAudio: input.transcribeAudio,
      quietWindowMinutes: input.quietWindowMinutes,
      historyWindowMessages: input.historyWindowMessages,
      historyWindowChars: input.historyWindowChars,
      dailyRunCap: input.dailyRunCap,
      digestEnabled: input.digestEnabled,
      digestTime: input.digestTime,
      timeZoneId: input.timeZoneId,
      digestRecipientNumber: input.digestRecipientNumber,
    },
  })
  ensureOk(result.response, result.error)
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapSettings(result.data)
}

export async function runTriageNow(tenantId: string, conversationSeq: number): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/triage/conversations/{conversationSeq}/run-now',
    { params: { path: { tenantId, conversationSeq } } },
  )
  ensureOk(result.response, result.error)
}

/** Envia o resumo diário agora (para o dono conferir); 409 quando não há canal conectado ou número. */
export async function sendDigestNow(tenantId: string): Promise<void> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/triage/digest/send-now', {
    params: { path: { tenantId } },
  })
  ensureOk(result.response, result.error)
}
